import React, { useCallback, useMemo, useState } from "react"
import { addElement, getCountText, getDefaultContentLanguage, removeElementById } from "../../../globalStates/utils"
import { EMPTY_NEWS, useExhibitorDataState } from "../../../globalStates/ExhibitorDataState"
import { addValidatorsNews } from "../../../globalStates/ExhibitorValidation"
import { useLanguageState } from "../../../globalStates/LanguageState"
import { loadQrCodeInfo, useConfigDataState } from "../../../globalStates/ConfigDataState"
import { newId } from "../../../utils/Id"
import Page from "../../../components/Page"
import NewsSection from "../NewsSection"
import { QrCodeEntityType, News, QrCodeResult } from "../../../globalStates/ExhibitorDataTypes"
import ButtonWithCaption from "../../../components/ButtonWithCaption"
import ModalQrCode from "../../../components/Modal/ModalQrCode"

const StoryNewsPage: React.FC<{}> = () => {
    const branding = useLanguageState().getStrings()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const storyNewsLimit = serviceKeys.stories || 0
    const storyNewsStates = useExhibitorDataState().getStoryNews()

    const [qrCodeModalVisible, setQrCodeModalVisible] = useState<boolean>(false)
    const [qrCodeUrl, setQrCodeUrl] = useState<string | undefined>()
    const [qrCodeName, setQrCodeName] = useState<string | undefined>()

    const canAddNews = useMemo(() => {
        if (storyNewsStates.length >= storyNewsLimit) return false
        let valid = true
        storyNewsStates.forEach((newState) => {
            const name = newState.name.get()
            const nameDe = newState.nameDe.get()

            getDefaultContentLanguage() === "De"
                ? (valid = valid && !!nameDe && nameDe.length >= 0)
                : (valid = valid && !!name && name.length >= 0)
        })
        return valid
        // eslint-disable-next-line
    }, [storyNewsLimit, storyNewsStates])

    const onAddNews = useCallback(() => {
        addElement<News>(storyNewsStates, { ...EMPTY_NEWS, id: newId().toString(), medias: [], type: "STORY" })
        addValidatorsNews(storyNewsStates[storyNewsStates.length - 1])
    }, [storyNewsStates])

    const onClickQrCode = useCallback((newsId: string) => {
        loadQrCodeInfo(QrCodeEntityType.NEWS, newsId)
            .then((response: QrCodeResult) => {
                setQrCodeName(response.entityName)
                setQrCodeUrl(response.url)
                setQrCodeModalVisible(true)
            })
            .catch((error: any) => {
                setQrCodeName(undefined)
                setQrCodeUrl(undefined)
                setQrCodeModalVisible(true)
            })
    }, [])

    const onRemoveNews = useCallback(
        (newsId: string) => {
            removeElementById<News>(storyNewsStates, newsId)
        },
        [storyNewsStates]
    )

    return (
        <Page description={branding.configuration.storyNewsPage.pageDescription}>
            {storyNewsStates?.map((newsState, index) => (
                <NewsSection
                    key={newsState.id.get()}
                    newsState={newsState}
                    index={index}
                    onClickQrCode={onClickQrCode}
                    onClickRemove={onRemoveNews}
                    categoryWhitelist={branding.configuration.storyNewsPage.categoryWhitelist}
                />
            ))}

            <ButtonWithCaption
                id="addStoryNews"
                caption={branding.showCounter ? getCountText(storyNewsStates.length, serviceKeys.stories || 0) : undefined}
                buttonText={branding.configuration.storyNewsPage.btnAddNews}
                onClick={onAddNews}
                disabled={!canAddNews}
            />

            {qrCodeModalVisible && (
                <ModalQrCode
                    entityType={QrCodeEntityType.NEWS}
                    name={qrCodeName}
                    url={qrCodeUrl}
                    visible={qrCodeModalVisible}
                    onClose={() => {
                        setQrCodeModalVisible(false)
                    }}
                />
            )}
        </Page>
    )
}

export default StoryNewsPage
